import type * as React from "react";
import { Box } from "../../components/ui/box";
import type { Color } from "../../lib/colors";
import type { Omittable } from "../../lib/props";

export type DiscoveryIndicatorPlacement = "top-right" | "inline-right";

export type DiscoveryIndicatorProps = Omittable<{
  /** When false, the dot is not rendered. Default true. */
  visible?: boolean;
  /**
   * Dot color — any library color (semantic or palette). Default `primary`.
   * Drives both the solid center dot and the pulsing halo.
   */
  color?: Color;
  /**
   * When true (default), a same-color halo pulses out from the center and
   * fades, drawing the eye. When false, only the solid center dot renders.
   */
  halo?: boolean;
  /**
   * Where the dot sits relative to the children:
   * - `top-right` (default): absolute, top-right corner. Best for icons and
   *   icon buttons.
   * - `inline-right`: a sibling at the right edge of a row. Best for text rows
   *   where the dot should follow the text content.
   */
  placement?: DiscoveryIndicatorPlacement;
  children: React.ReactNode;
  ref?: React.Ref<HTMLDivElement>;
}>;

const DOT_SIZE = 8;

/**
 * The dot itself: a solid center dot plus an optional pulsing halo. The halo
 * is an absolutely-positioned sibling sharing the dot's color; the
 * `discovery-halo` keyframe grows it from the center and fades it out.
 */
function Dot({ color, halo }: { color: Color; halo: boolean }) {
  return (
    // `data-color` sits on the wrapper: custom properties inherit, so one
    // declaration tints both the dot and the halo from `bg-c`.
    <Box pointerEvents="none"
      data-color={color}
      height={DOT_SIZE}
      position="relative"
      width={DOT_SIZE}
    >
      {halo ? (
        <Box
          className="bg-c animate-discovery-halo"
          height={DOT_SIZE}
          left={0}
          position="absolute"
          rounded="full"
          top={0}
          width={DOT_SIZE}
        />
      ) : null}
      <Box className="bg-c" height={DOT_SIZE} rounded="full" width={DOT_SIZE} />
    </Box>
  );
}

/**
 * Wraps a child element and renders a small colored dot with a soft pulsing
 * halo. Use it to draw attention to a new or unseen feature.
 *
 * Extra props land on the outer Box, so this composes as the `render` target
 * of Base UI triggers (MenuTrigger, DialogTrigger, …).
 */
export function DiscoveryIndicator({
  visible = true,
  color = "primary",
  halo = true,
  placement = "top-right",
  children,
  ...rest
}: DiscoveryIndicatorProps & Omit<React.ComponentProps<"div">, "color">) {
  if (placement === "inline-right") {
    return (
      <Box align="center" direction="row" gap="sm" {...rest}>
        {children}
        {visible ? <Dot color={color} halo={halo} /> : null}
      </Box>
    );
  }

  return (
    <Box position="relative" {...rest}>
      {children}
      {visible ? (
        <Box pointerEvents="none"
          position="absolute"
          right={-1}
          top={-1}
        >
          <Dot color={color} halo={halo} />
        </Box>
      ) : null}
    </Box>
  );
}
